import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { musicCreditsAPI } from '@/lib/api';
import { Gift, Check, X, Lock } from 'lucide-react';
import { toast } from 'sonner';

const PendingTransferAlert = () => {
  const queryClient = useQueryClient();
  const [pins, setPins] = useState({});  // transfer_id -> pin input

  const { data: transfers = [] } = useQuery({
    queryKey: ['pending-transfers'],
    queryFn: async () => (await musicCreditsAPI.getPendingTransfers()).data,
    refetchInterval: 30000,  // poll every 30 seconds
  });

  const acceptMut = useMutation({
    mutationFn: (data) => musicCreditsAPI.acceptTransfer(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['pending-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['student-music-credits'] });
      queryClient.invalidateQueries({ queryKey: ['guardian-music-credits'] });
      queryClient.invalidateQueries({ queryKey: ['student-media-library'] });
      toast.success(res.data.message);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to accept transfer'),
  });

  const declineMut = useMutation({
    mutationFn: (data) => musicCreditsAPI.declineTransfer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-transfers'] });
      toast.success('Transfer declined. Credits returned to sender.');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to decline'),
  });

  if (transfers.length === 0) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="pending-transfers">
      {transfers.map(t => (
        <div key={t.id} className="flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200"
          data-testid={`transfer-${t.id}`}>
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Gift size={20} className="text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm text-gray-900">
              {t.sender_name} sent you <span className="text-indigo-600">{t.credits} music credits</span>
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              From {t.sender_email} &middot; {new Date(t.created_date).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </p>
            <p className="text-[10px] text-amber-700 mt-1 font-medium">
              Enter your PIN to accept these credits into your account.
            </p>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 px-2 py-1">
                <Lock size={12} className="text-gray-400" />
                <input type="password" maxLength="6" placeholder="PIN"
                  value={pins[t.id] || ''}
                  onChange={(e) => setPins({ ...pins, [t.id]: e.target.value })}
                  className="w-16 text-sm font-bold text-center bg-transparent outline-none"
                  data-testid={`pin-input-${t.id}`} />
              </div>
              <button onClick={() => acceptMut.mutate({ transfer_id: t.id, pin: pins[t.id] || '' })}
                disabled={acceptMut.isPending || !(pins[t.id]?.length >= 4)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 transition-all disabled:opacity-40"
                data-testid={`accept-${t.id}`}>
                <Check size={12} /> Accept
              </button>
              <button onClick={() => { if (window.confirm('Decline this transfer? Credits will be returned to the sender.')) declineMut.mutate({ transfer_id: t.id }); }}
                disabled={declineMut.isPending}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-bold hover:bg-gray-200 transition-all"
                data-testid={`decline-${t.id}`}>
                <X size={12} /> Decline
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PendingTransferAlert;
